import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import './index.less';

import { AgentModel, AgentState, DataInterfaceType, ToolsetModel } from '../../../../../gamma-api';
import { defaultApi } from '../../../../apis';
import { getAgentPath, getAgentToolsetsPath } from '../../../../utils/pathUtils';
import IbCard from '../../../../components/common/IbCard';
import IbContextMenu, { IIbContextMenuItem } from '../../../../components/common/IbContextMenu';
import IbButton from '../../../../components/common/IbButton';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import IbTooltip from '../../../../components/common/IbTooltip';
import IbDivider from '../../../../components/common/IbDivider';
import { formatDateFull, getTranslateFunc } from '../../../../utils/stringUtil';
import { getToolsetTypeIconName, getToolsetTypeName } from '../../AgentPage/ToolsetsTab/utils';
import IbBadge from '../../../../components/common/IbBadge';
import Avatar, { AvatarName } from '../../../../components/common/Avatar';
import IbSwitch from '../../../../components/common/IbSwitch';
import ExportButton from '../../../../components/ExportButton';
import ImportUpload from '../../../../components/ImportUpload';

import {
  CONTENT_CLASS_NAME,
  DISABLED_CLASS_NAME,
  EXTRA_CLASS_NAME,
  FOOTER_CLASS_NAME,
  INSTRUCTIONS_CLASS_NAME,
  MAIN_CLASS_NAME,
  TOOLSET_LIST_CLASS_NAME,
  TOOLSET_LIST_ITEM_CLASS_NAME,
  TOOLSETS_PAGE_SIZE,
  TOOLTIP_CLASS_NAME,
  TRANSLATION_GROUP_PREFIX,
} from './const';

interface IAgentCardProps {
  agent: AgentModel;
  searchText: string;
  onEditAgent: () => void;
  onDeleteAgent: () => void;
  onAgentClick: () => void;
  onAgentStateToggle: () => Promise<void>;
}

const AgentCard: React.FC<IAgentCardProps> = ({
  agent,
  searchText,
  onEditAgent,
  onDeleteAgent,
  onAgentClick,
  onAgentStateToggle,
}) => {
  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);
  const { i18n } = useTranslation();

  const { push } = useHistory();

  const [contextMenuVisible, setContextMenuVisible] = useState(false);

  const [toolsetCount, setToolsetCount] = useState<number>(0);
  const [toolsetList, setToolsetList] = useState<ToolsetModel[]>([]);

  const getToolsetCountAsync = async () => {
    try {
      const response = await defaultApi.searchToolsets(undefined, undefined, [agent.agent_id], 0, TOOLSETS_PAGE_SIZE);
      setToolsetCount(response.data.total_item_count ?? 0);
      setToolsetList(response.data.items ?? []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onInitialLoad = () => {
    getToolsetCountAsync().finally();
  };
  useEffect(onInitialLoad, []);

  const goToAgent = () => {
    push(getAgentPath(agent.settings.interface.type, agent.folder_id, agent.agent_id));
  };

  const onImportFileFinish = async () => {
    goToAgent();
  };

  const contextMenuItems = [
    {
      icon: <IbIcon iconName="efferent-three" size={20} />,
      text: translate('Context: Export'),
      render: (child) => (
        <ExportButton.AgentExport agentId={agent.agent_id} interfaceType={agent.settings.interface.type}>
          {child}
        </ExportButton.AgentExport>
      ),
    },
    {
      icon: <IbIcon iconName="afferent-three" size={20} />,
      text: translate('Context: Import'),
      render: (child) => (
        <ImportUpload.AgentImport
          agentId={agent.agent_id}
          folderId={agent.folder_id}
          interfaceType={agent.settings.interface.type}
          onFinish={onImportFileFinish}
        >
          {child}
        </ImportUpload.AgentImport>
      ),
    },
    {
      icon: <IbIcon iconName="edit" size={20} />,
      onSelect: onEditAgent,
      text: translate('Context: Edit'),
    },
    {
      icon: <IbIcon iconName="delete" size={20} />,
      onSelect: onDeleteAgent,
      text: translate('Context: Delete'),
    },
  ] as IIbContextMenuItem[];

  const renderInstructionsTooltip = (title: string) => {
    return <div className={TOOLTIP_CLASS_NAME}>{title}</div>;
  };

  const renderExtra = () => {
    const onContextMenuClose = () => setContextMenuVisible(false);
    const onMenuButtonClick = () => setContextMenuVisible(true);
    return (
      <div className={EXTRA_CLASS_NAME} onClick={(e) => e.stopPropagation()}>
        <IbSwitch checked={agent.state === AgentState.Enabled} onChange={onAgentStateToggle} />
        <IbContextMenu menuItems={contextMenuItems} visible={contextMenuVisible} onClose={onContextMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" size={24} />} type="icon" onClick={onMenuButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  const renderInstructions = () => {
    const instructions = agent.settings.instructions || '';
    return (
      <div className={INSTRUCTIONS_CLASS_NAME}>
        <IbTooltip title={renderInstructionsTooltip(instructions)}>
          <IbTypography.Paragraph>{instructions}</IbTypography.Paragraph>
        </IbTooltip>
      </div>
    );
  };

  const onShowAllToolsetsButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    push(getAgentToolsetsPath(agent.settings.interface.type, agent.folder_id, agent.agent_id));
  };

  const renderToolsetList = () => {
    const toolsets = toolsetCount > TOOLSETS_PAGE_SIZE ? toolsetList.slice(0, TOOLSETS_PAGE_SIZE - 1) : toolsetList;
    return (
      <div className={TOOLSET_LIST_CLASS_NAME}>
        {toolsets.map((toolset) => (
          <div key={toolset.toolset_id} className={TOOLSET_LIST_ITEM_CLASS_NAME}>
            <IbIcon iconName={getToolsetTypeIconName(toolset.type)} size={16} />
            <IbTypography.Paragraph ellipsis type="secondary">
              {getToolsetTypeName(toolset.type)}
            </IbTypography.Paragraph>
          </div>
        ))}
        {toolsetCount > TOOLSETS_PAGE_SIZE && (
          <IbButton icon={<IbBadge value={toolsetCount} />} type="link" onClick={onShowAllToolsetsButtonClick}>
            {translate('Tools: Show all')}
          </IbButton>
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <IbTypography className={CONTENT_CLASS_NAME}>
        {renderInstructions()}
        {renderToolsetList()}
      </IbTypography>
    );
  };

  const renderFooter = () => {
    return (
      <div className={FOOTER_CLASS_NAME}>
        <IbTypography.Paragraph disabled>{translate('Tools: Count', { count: toolsetCount })}</IbTypography.Paragraph>
        <IbDivider orientation="center" type="vertical" />
        <IbTypography.Paragraph disabled>
          {translate('Modified on', { date: formatDateFull(agent.modified_on, i18n.language) })}
        </IbTypography.Paragraph>
      </div>
    );
  };

  const getDisplayName = () => {
    if (agent.settings.interface.type === DataInterfaceType.Chat) {
      return agent.settings.widget.display_name;
    }

    return undefined;
  };

  const renderAvatar = () => {
    if (agent.settings.interface.type === DataInterfaceType.Api) return;
    return <Avatar avatarName={agent.settings.widget.avatar.name as AvatarName} size={48} />;
  };

  const classes = [MAIN_CLASS_NAME];
  agent.state === AgentState.Disabled && classes.push(DISABLED_CLASS_NAME);

  return (
    <div className={classes.join(' ')}>
      <IbCard
        avatar={renderAvatar()}
        displayName={getDisplayName()}
        extra={renderExtra()}
        footer={renderFooter()}
        searchText={searchText}
        title={agent.name}
        onClick={onAgentClick}
      >
        {renderContent()}
      </IbCard>
    </div>
  );
};

export default AgentCard;
